import React, { useState, useContext, useEffect } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import { alpha, styled } from "@mui/material/styles";
import { Button, Input, TablePagination } from "@mui/material";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import { LoginContext } from "../../../contexts/loginContext";
import createInstance from "../../../api/axiosinstance";
import Modal from "../Modal";
import Toast from "../../../utils/toast";
import InputBase from "@mui/material/InputBase";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";
import { useThemeMode } from "../../Themes/ThemeContextTemp.js";

const aiDishaApi = createInstance("DISHA");

const SkillManagement = () => {
  const { isLoggedIn, adminId: admin_id } = useContext(LoginContext);
  const [skillName, setSkillName] = useState("");
  const [creationDate] = useState(new Date().toISOString());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [skillId, setSkillId] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editedName, setEditedName] = useState("");
  const [deactivateId, setDeactiveId] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortingOrder, setSortingOrder] = useState("asc");
  const [selectedSkill, setSelectedSkill] = useState(""); 
  const [searchTerm, setSearchTerm] = useState("");
  const [skillsList, setSkillsList] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const { COLORS } = useThemeMode();

  useEffect(() => {
    fetchSkillsForDropdown();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchSkillsForDropdown = async () => {
    try {
      const response = await aiDishaApi.get('/all-skill/');
      if (response.data) {
        setSkillsList(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching skills for dropdown:", error.message);
    }
  };

  useEffect(() => {
    if (skillName === "") {
      setPage(0);
    }
  }, [skillName]);

  const handleSort = (field) => {
    const sortedData = [...skillsList].sort((a, b) => {
      if (sortingOrder === "asc") {
        return a[field].localeCompare(b[field]);
      } else {
        return b[field].localeCompare(a[field]);
      }
    });
    setSkillsList(sortedData);
    setSortingOrder(sortingOrder === "asc" ? "desc" : "asc");
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  // FIX ISSUE 28: Centralized filtered list with .trim() for robust searching
  const filteredSkillsList = skillsList ? skillsList.filter((row) =>
    row.skill_name && row.skill_name.toLowerCase().includes(searchTerm.trim().toLowerCase())
  ) : [];

  const handleSkillSubmit = async () => {
    try {
      const trimmedName = skillName.trim();

      if (!trimmedName) {
        Toast.error("Skill name cannot be empty");
        return;
      }

      // FIX ISSUE 27 & 33: Prevent special-character-only and empty-space values
      const isValidName = /^[a-zA-Z0-9\s]+$/.test(trimmedName);
      if (!isValidName) {
        Toast.error("Please enter a valid Skill Name. Special characters are not allowed.");
        return;
      }

      // FIX ISSUES 29 & 30: Case-insensitive and space-trimmed duplicate check
      const existingSkill = skillsList.find(
        (skill) => skill.skill_name.trim().toLowerCase() === trimmedName.toLowerCase()
      );
      if (existingSkill) {
        Toast.error("Skill Name already exists.");
        return;
      }

      const formData = new FormData();
      formData.append("skill_name", trimmedName);
      formData.append("status", "Active");

      const response = await aiDishaApi.post("/admin-skills/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status === 200 || response.status === 201) {
        Toast.success("Skill added successfully");
        setSkillName(""); 
        setSelectedSkill(""); 
        fetchSkillsForDropdown();
      }
    } catch (error) {
      if (error.response?.status === 409) {
        Toast.error("Skill Name already exists.");
      } else {
        Toast.error("Failed to add skill. Please try again.");
      }
      console.error("Error:", error);
    }
  };

  const handleEdit = (id, name) => {
    setSkillId(id);
    setEditedName(name);
    setSkillName(name);
    setIsEdit(true);
  };

  const getErrorMessage = (error, fallback) => {
    const detail = error.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      return detail.map((d) => d.msg).join(", ");
    }
    return error.response?.data?.message || fallback;
  };

  const handleSkillEdit = async (skill_id) => {
    try {
      const trimmedName = skillName.trim();

      if (!trimmedName) {
        Toast.error("Skill name cannot be empty");
        return;
      }

      // FIX ISSUE 33: Prevent special-character-only and empty-space values during edit
      const isValidName = /^[a-zA-Z0-9\s]+$/.test(trimmedName);
      if (!isValidName) {
        Toast.error("Please enter a valid Skill Name. Special characters are not allowed.");
        return;
      }

      // FIX ISSUES 31 & 32: Case-insensitive duplicate check during edit
      const existingSkill = skillsList.find(
        (skill) =>
          skill.skill_name.trim().toLowerCase() === trimmedName.toLowerCase() &&
          skill.skill_id !== skill_id
      );
      if (existingSkill) {
        Toast.error("Skill Name already exists.");
        return;
      }

      const formData = new FormData();
      formData.append("skill_name", trimmedName);

      const updateResponse = await aiDishaApi.put(
        `/admin-skills-update/${skill_id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (updateResponse.status === 200) {
        Toast.success("Skill updated successfully");
        setSkillName("");
        fetchSkillsForDropdown();
        setIsEdit(false);
      }
    } catch (error) {
      Toast.error(getErrorMessage(error, "Failed to update skill"));
      console.error("Error:", error);
    } finally {
      setEditingRowId(null);
    }
  };

  const handleClear = () => {
    setSkillName("");
    setSelectedSkill("");
    setSkillId(null);
    setSearchTerm("");
    setIsEdit(false);
    setEditingRowId(null);
  };

  const handleModalOpen = (skill_id, status) => {
    setDeactiveId(skill_id);
    setSelectedStatus(status);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleDeactivateSkill = async (skill_id, currentStatus) => {
    try {
      const newStatus = currentStatus === "Active" ? "Inactive" : "Active";
      const response = await aiDishaApi.patch(
        `/update-admin-skills-status/${skill_id}/status`,
        null, 
        { params: { status_update: newStatus } }
      );

      if (response.status === 200) {
        Toast.success("Skill status updated successfully");
        fetchSkillsForDropdown();
        handleModalClose();
      }
    } catch (error) {
      console.error("Error:", error);
      Toast.error("Failed to update skill status");
    }
  };

  const skillHeader = [
    { id: "Skill ID", name: "Skill Name", creationDate: "Creation Date", action: "Action" },
  ];

  const CssTextField = styled(TextField)({
    "& label": { color: "#FEFEFE" },
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "#B9B9B9", borderRadius: 10 },
      "&:hover fieldset": { borderColor: "#B9B9B9" },
      "&.Mui-focused fieldset": { borderColor: "#B9B9B9" },
      "& .MuiInputBase-input": { color: "#FEFEFE" },
    },
  });

  return (
    <div>
      {isEdit ? (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, 1fr)"
          sx={{
            marginTop: "30px",
            alignItems: "center",
            background: COLORS.primary,
            borderRadius: "10px",
            padding: "15px",
          }}
        >
          <Box gridColumn="span 6" sx={{ marginTop: "auto", textAlign: "left", marginBottom: "20px" }}>
            <Typography sx={{ fontFamily: "poppins", fontSize: "28px", color: COLORS.text }}>
              Edit Skill
            </Typography>
          </Box>
          <Box gridColumn="span 6" sx={{ marginTop: "auto", textAlign: "left", marginBottom: "2%" }}>
            <Paper sx={{ p: "2px 4px", display: "flex", width: "75%", borderRadius: 2, backgroundColor: COLORS.background }}>
              <IconButton type="button" sx={{ p: "8px", color: COLORS.text }} aria-label="search">
                <SearchIcon />
              </IconButton>
              <InputBase
                sx={{ ml: 1, flex: 1, fontFamily: "Poppins", color: COLORS.text, "& input::placeholder": { color: `${COLORS.text} !important`, opacity: 10 } }}
                placeholder="Search by Skill Name"
                value={searchTerm}
                onChange={handleSearch}
              />
            </Paper>
          </Box>

          <Box gridColumn="span 5" sx={{ marginTop: "auto", textAlign: "left" }}>
            <Typography sx={{ fontFamily: "poppins", color: COLORS.text }}>
              Skill Name <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              size="small"
              variant="outlined"
              value={skillName}
              onChange={(e) => setSkillName(e.target.value)}
              sx={{
                background: COLORS.background,
                borderRadius: 2,
                width: "95%",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.border, borderRadius: "10px" },
                "&.MuiOutlinedInput-root.Mui-focused.MuiOutlinedInput-notchedOutline": { borderColor: COLORS.border },
                "& .MuiInputLabel-root": { color: COLORS.text },
                "& .MuiInputBase-input": { color: COLORS.text },
              }}
              required
            />
          </Box>

          <Box gridColumn="span 3" sx={{ marginTop: "auto", textAlign: "left", marginLeft: 10 }}>
            <Button onClick={handleClear} sx={{ border: "1px solid", color: COLORS.button, "&:hover": { color: COLORS.hover_btn }, width: "150px", height: "40px", borderRadius: "10px", fontFamily: "poppins" }}>
              Clear
            </Button>
          </Box>
          <Box gridColumn="span 3" sx={{ marginTop: "auto", textAlign: "left", marginLeft: 10 }}>
            <Button sx={{ background: COLORS.button, color: COLORS.background, "&:hover": { background: COLORS.hover_btn }, width: "150px", height: "40px", borderRadius: "10px", fontFamily: "poppins" }} onClick={() => handleSkillEdit(skillId)}>
              <Typography sx={{ fontFamily: "poppins", color: COLORS.background }}>Save</Typography>
            </Button>
          </Box>
        </Box>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, 1fr)"
          sx={{
            marginTop: "30px",
            alignItems: "center",
            background: COLORS.primary,
            borderRadius: "10px",
            padding: "15px",
          }}
        >
          <Box gridColumn="span 6" sx={{ marginTop: "auto", textAlign: "left", marginBottom: "20px" }}>
            <Typography sx={{ fontFamily: "poppins", fontSize: "28px", color: COLORS.text }}>
              Add Skill
            </Typography>
          </Box>
          <Box gridColumn="span 6" sx={{ marginTop: "auto", textAlign: "left", marginBottom: "2%" }}>
            <Paper sx={{ p: "2px 4px", display: "flex", width: "75%", borderRadius: 2, background: COLORS.background }}>
              <IconButton type="button" sx={{ p: "8px", color: COLORS.text }} aria-label="search">
                <SearchIcon />
              </IconButton>
              <InputBase
                sx={{ ml: 1, flex: 1, fontFamily: "Poppins", color: COLORS.text, "& input::placeholder": { color: `${COLORS.text} !important`, opacity: 10 } }}
                placeholder="Search by Skill Name"
                value={searchTerm}
                onChange={handleSearch}
              />
            </Paper>
          </Box>

          <Box gridColumn="span 5" sx={{ marginTop: "auto", textAlign: "left" }}>
            <Typography sx={{ fontFamily: "poppins", color: COLORS.text }}>
              Skill Name <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              size="small"
              variant="outlined"
              value={skillName}
              onChange={(e) => setSkillName(e.target.value)}
              sx={{
                background: COLORS.background,
                borderRadius: 2,
                width: "95%",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.border, borderRadius: "10px" },
                "&.MuiOutlinedInput-root.Mui-focused.MuiOutlinedInput-notchedOutline": { borderColor: COLORS.border },
                "& .MuiInputLabel-root": { color: COLORS.text },
                "& .MuiInputBase-input": { color: COLORS.text },
              }}
              required
            />
          </Box>

          <Box gridColumn="span 3" sx={{ marginTop: "auto", textAlign: "left", marginLeft: 10 }}>
            <Button onClick={handleClear} sx={{ border: "1px solid", color: COLORS.button, "&:hover": { color: COLORS.hover_btn }, width: "150px", height: "40px", borderRadius: "10px", fontFamily: "poppins" }}>
              Clear
            </Button>
          </Box>
          <Box gridColumn="span 3" sx={{ marginTop: "auto", textAlign: "left", marginLeft: 10 }}>
            <Button sx={{ background: COLORS.button, color: COLORS.background, "&:hover": { background: COLORS.hover_btn }, width: "150px", height: "40px", borderRadius: "10px", fontFamily: "poppins" }} onClick={handleSkillSubmit}>
              <Typography sx={{ fontFamily: "poppins", color: COLORS.background }}>Save</Typography>
            </Button>
          </Box>
        </Box>
      )} 

      <Box xs={1} sx={{ backgroundColor: "#33333" }}>
        <Grid container spacing={1} sx={{ justifyContent: "center" }}>
          <Grid item xs={10}>
            <TableContainer component={Paper} sx={{ marginTop: "25px", borderRadius: "10px" }}>
              <Table size="small">
                <TableHead sx={{ background: COLORS.primary }}>
                  {skillHeader.map(({ id, name, creationDate, action }) => (
                    <React.Fragment key={id}>
                      <TableCell sx={{ color: COLORS.text, borderBottom: 0 }}>{id}</TableCell>
                      <TableCell sx={{ color: COLORS.text, borderBottom: 0, cursor: "pointer" }} onClick={() => handleSort("skill_name")}>
                        {name} {sortingOrder === "asc" ? "↑" : "↓"}
                      </TableCell>
                      <TableCell sx={{ color: COLORS.text, borderBottom: 0 }}>{creationDate}</TableCell>
                      <TableCell sx={{ color: COLORS.text, borderBottom: 0 }}>{action}</TableCell>
                    </React.Fragment>
                  ))}
                </TableHead>
                <TableBody>
                  {/* FIX ISSUE 26: Enhanced "No records found" UI logic */}
                  {filteredSkillsList.length > 0 ? (
                    filteredSkillsList
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((n, index) => (
                        <TableRow key={n.skill_id} sx={{ background: COLORS.background, marginRight: "30px" }}>
                          <TableCell sx={{ color: COLORS.text, border: COLORS.secondary }}>{n.skill_id}</TableCell>
                          <TableCell sx={{ color: COLORS.text, border: COLORS.secondary }}>{n.skill_name}</TableCell>
                          <TableCell sx={{ color: COLORS.text, border: COLORS.secondary }}>
                            {new Date(n.creation_date).toLocaleDateString("en-GB")}
                          </TableCell>
                          <TableCell sx={{ borderBottom: COLORS.secondary, borderRight: COLORS.secondary, display: "flex", alignItems: "center", fontFamily: "poppins" }}>
                            {editingRowId === n.skill_id ? (
                              <Button variant="contained" sx={{ background: COLORS.button_edit_bg, "&:hover": { background: COLORS.hover_edit_btn }, color: COLORS.background, marginRight: "10px", fontFamily: "poppins", height: "20px", width: "30px" }}>
                                Save
                              </Button>
                            ) : (
                              <Button variant="contained" onClick={() => handleEdit(n.skill_id, n.skill_name)} sx={{ background: COLORS.button_edit_bg, "&:hover": { background: COLORS.hover_edit_btn }, color: COLORS.background, marginRight: "10px", fontFamily: "poppins", height: "20px", width: "30px" }}>
                                Edit
                              </Button>
                            )}
                            <Button
                              variant="contained"
                              sx={{
                                background: n.status === "Active" ? COLORS.button_red_bg : COLORS.button_green_bg,
                                color: COLORS.text,
                                fontFamily: "poppins",
                                height: "24px",
                                padding: "2px 8px",
                                minWidth: "90px",
                                fontSize: "0.75rem",
                                "&:hover": { background: n.status === "Active" ? COLORS.hover_red_btn : COLORS.hover_green_btn },
                              }}
                              onClick={() => handleModalOpen(n.skill_id, n.status)}
                              color="error"
                            >
                              {n.status === "Active" ? "Deactivate" : "Activate"}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow sx={{ background: COLORS.background }}>
                      <TableCell colSpan={4} sx={{ textAlign: "center", color: COLORS.text, padding: "20px" }}>
                        No records found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={filteredSkillsList.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ background: COLORS.primary, color: COLORS.text }}
              />
            </TableContainer>
          </Grid>
        </Grid>
      </Box>
      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title="Skill"
        action={() => handleDeactivateSkill(deactivateId, selectedStatus)}
        actionType={selectedStatus === "Active" ? "Deactivate" : "Activate"}
      />
    </div>
  );
};

export default SkillManagement;